import { Component } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import { MODAL } from '../constants'
import Portal from './Portal'

let modalTitleSeq = 0

// Covers the viewport from `#portal`. Rendered where it is opened it would instead cover only the
// card it was opened from, which owns a backdrop-filter and so becomes the containing block for
// anything fixed inside it, and the cards below would paint over the dialog.
const ModalBox = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: ${MODAL};
`

const ModalContent = styled.div`
  position: relative;
  margin: 1.5rem;
  border: 1px solid #979797;
  width: 100%;
  max-width: 640px;
  max-height: ${p => (p.$heightRestricted ? p.$maxHeight : 'calc(100vh - 3rem)')};
  overflow-y: ${p => (p.$heightRestricted ? 'visible' : 'auto')};
  background-color: white;
  padding: 30px;
`

const Title = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 2.5rem;
  display: flex;
  line-height: 50px;
  font-weight: 300;
  text-align: left;
  color: ${p => p.theme.mediumGray};
  transition: 0.8s;
  padding-top: 10px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  @media (max-width: 520px) {
    font-size: 2rem;
    line-height: 42px;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  padding: 10px;
  border: none;
  background: none;
  color: ${p => p.theme.lightestGray};
  font-size: 50px;
  line-height: 30px;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
`

export default class SharedModal extends Component {
  static propTypes = {
    isModalOpen: PropTypes.bool,
    toggleModal: PropTypes.func.isRequired,
    title: PropTypes.string,
    children: PropTypes.node,
    maxHeight: PropTypes.number,
    isHeightRestricted: PropTypes.bool,
  }

  titleId = `shared-modal-title-${(modalTitleSeq += 1)}`
  previousOverflow = ''

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    if (this.props.isModalOpen) this.lockBodyScroll()
  }

  componentDidUpdate(prevProps) {
    if (this.props.isModalOpen === prevProps.isModalOpen) return
    if (this.props.isModalOpen) this.lockBodyScroll()
    else this.unlockBodyScroll()
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    if (this.props.isModalOpen) this.unlockBodyScroll()
  }

  lockBodyScroll() {
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  unlockBodyScroll() {
    document.body.style.overflow = this.previousOverflow
  }

  handleKeyDown = event => {
    if (event.key === 'Escape' && this.props.isModalOpen) this.props.toggleModal()
  }

  render() {
    const {
      isModalOpen,
      toggleModal,
      title,
      children,
      maxHeight,
      isHeightRestricted = true,
    } = this.props

    if (!isModalOpen) return null

    return (
      <Portal>
        <ModalBox>
          <ModalContent
            role='dialog'
            aria-modal='true'
            aria-labelledby={title ? this.titleId : undefined}
            $heightRestricted={isHeightRestricted}
            $maxHeight={`${maxHeight ?? 480}px`}>
            <CloseButton type='button' aria-label='Close' onClick={toggleModal}>
              &times;
            </CloseButton>
            {title && <Title id={this.titleId}>{title}</Title>}
            {children}
          </ModalContent>
        </ModalBox>
      </Portal>
    )
  }
}
